/**
 * Zod helpers for the `PlannedDate` value kind.
 *
 * Used both to validate inbound API responses (the backend always returns the
 * object shape) and to validate form values before they are sent. Mirrors the
 * backend invariants: `timeZoneId` is only allowed together with `time`, and must
 * be a resolvable IANA identifier.
 *
 * Error messages are i18n keys (resolved by the consuming form component). The
 * only consumers today are the tasks forms, which load the `tasks` namespace.
 */

import { z } from 'zod';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}$/;

/** True when `id` resolves as a system IANA time zone (dependency-free check). */
export function isValidTimeZoneId(id: string): boolean {
  try {
    // Throws RangeError for unknown identifiers.
    new Intl.DateTimeFormat('en-US', { timeZone: id });
    return true;
  } catch {
    return false;
  }
}

/** `yyyy-MM-dd` calendar date. */
export function zPlainDate() {
  return z.string().regex(DATE_RE, { error: 'tasks.datePicker.errors.invalidDate' });
}

/** `HH:mm` (24h) wall-clock time. */
export function zPlainTime() {
  return z.string().regex(TIME_RE, { error: 'tasks.datePicker.errors.invalidTime' });
}

/**
 * A `PlannedDate` object: required `date`, optional `time`, optional IANA
 * `timeZoneId`. Enforces `timeZoneId` ⇒ `time`, and a valid IANA id.
 */
export function zPlannedDate() {
  return z
    .object({
      date: zPlainDate(),
      time: zPlainTime().optional(),
      timeZoneId: z.string().optional(),
    })
    .superRefine((value, ctx) => {
      if (value.timeZoneId != null && value.time == null) {
        ctx.addIssue({
          code: 'custom',
          message: 'tasks.datePicker.errors.timeZoneWithoutTime',
          path: ['timeZoneId'],
        });
      }
      if (value.timeZoneId != null && !isValidTimeZoneId(value.timeZoneId)) {
        ctx.addIssue({
          code: 'custom',
          message: 'tasks.datePicker.errors.invalidTimeZone',
          path: ['timeZoneId'],
        });
      }
    });
}

export type ZPlannedDate = z.infer<ReturnType<typeof zPlannedDate>>;
